import type { Permission } from '@cartly/shared';
import {
  BarChart3,
  Bot,
  CreditCard,
  Inbox,
  Camera,
  LayoutDashboard,
  MessageCircle,
  PhoneCall,
  Settings,
  ShoppingBag,
  Store,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';

export interface NavChild {
  label: string;
  path: string;
  permission?: Permission;
}

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  /** Implementation phase that delivers the module (docs/IMPLEMENTATION_PLAN.md). */
  phase: number;
  permission?: Permission;
  /** Built ahead of CURRENT_PHASE: the module has a real page even though its phase is not complete. */
  live?: boolean;
  description: string;
  /** Sub-pages shown under the item in a collapsible group. */
  children?: NavChild[];
}

export interface NavSection {
  title: string | null;
  items: NavItem[];
}

/** Modules available in the current build. Others render a "not yet available" page. */
export const CURRENT_PHASE = 6;

export const NAV: NavSection[] = [
  {
    title: null,
    items: [
      {
        label: 'Overview',
        path: '/',
        icon: LayoutDashboard,
        phase: 1,
        description: 'Key metrics and setup progress.',
      },
    ],
  },
  {
    title: 'Commerce',
    items: [
      {
        label: 'Stores',
        path: '/stores',
        icon: Store,
        phase: 2,
        permission: 'stores:read',
        description:
          'Connect Shopify, WooCommerce and custom stores through the Universal Commerce API.',
      },
      {
        label: 'Customers',
        path: '/customers',
        icon: Users,
        phase: 2,
        permission: 'customers:read',
        description: 'Customer profiles with orders and carts across your stores.',
      },
      {
        label: 'Orders',
        path: '/orders',
        icon: ShoppingBag,
        phase: 2,
        permission: 'orders:read',
        description: 'Orders and checkouts synchronized from every connected store.',
      },
    ],
  },
  {
    title: 'Engage',
    items: [
      {
        label: 'Unified Inbox',
        path: '/inbox',
        icon: Inbox,
        phase: 6,
        live: true,
        permission: 'inbox:read',
        description:
          'WhatsApp and Instagram conversations in one place, with assignment and AI suggestions.',
      },
      {
        label: 'WhatsApp',
        path: '/whatsapp',
        icon: MessageCircle,
        phase: 4,
        live: true,
        permission: 'channels:read',
        description: 'WhatsApp Cloud API numbers, templates, opt-ins and transactional messaging.',
        children: [
          { label: 'Accounts', path: '/whatsapp' },
          { label: 'Templates', path: '/whatsapp/templates' },
          { label: 'Messages', path: '/whatsapp/messages' },
          { label: 'Campaigns', path: '/whatsapp/campaigns', permission: 'campaigns:read' },
          { label: 'Alerts', path: '/whatsapp/alerts', permission: 'campaigns:read' },
          { label: 'Shopping', path: '/whatsapp/shopping', permission: 'campaigns:read' },
        ],
      },
      {
        label: 'Instagram',
        path: '/instagram',
        icon: Camera,
        phase: 6,
        live: true,
        permission: 'channels:read',
        description: 'Instagram messaging, comment-to-DM and keyword automations.',
      },
    ],
  },
  {
    title: 'Automate',
    items: [
      {
        label: 'Automations',
        path: '/automations',
        icon: Zap,
        phase: 5,
        live: true,
        permission: 'automations:read',
        description:
          'Build, test and publish WhatsApp flows, and follow every run, including abandoned-checkout recovery.',
      },
      {
        label: 'AI Assistant',
        path: '/ai-agents',
        icon: Bot,
        phase: 6,
        live: true,
        permission: 'ai:read',
        description: 'A shopping assistant grounded in your catalog, FAQs and documents.',
      },
      {
        label: 'AI Calling',
        path: '/ai-calling',
        icon: PhoneCall,
        phase: 7,
        live: true,
        permission: 'calls:read',
        description:
          'Voice agents for COD confirmation, order enquiries and support, with consent controls.',
      },
    ],
  },
  {
    title: 'Insights',
    items: [
      {
        label: 'Analytics',
        path: '/analytics',
        icon: BarChart3,
        phase: 7,
        permission: 'analytics:read',
        description: 'Recovered revenue, attribution, conversion and usage.',
      },
    ],
  },
  {
    title: 'Account',
    items: [
      {
        label: 'Billing',
        path: '/billing',
        icon: CreditCard,
        phase: 7,
        live: true,
        permission: 'billing:read',
        description: 'Plan, usage and payments.',
      },
      {
        label: 'Settings',
        path: '/settings',
        icon: Settings,
        phase: 1,
        description: 'Organization, team, roles, security and audit log.',
      },
    ],
  },
];

export const ALL_NAV_ITEMS = NAV.flatMap((s) => s.items);
